import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

import type { FeelingAtHomeWidgetV2Dto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { SUPPORT_EMAIL } from "@/helpers/constants";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import type { DateRange } from "@/queries/analytics";
import { useAnalyticsQueries } from "@/queries/analytics";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetStates } from "../AnalyticsWidgetStates";
import { FahWidgetGraphics } from "./FahWidgetGraphics";
import { FahWidgetSpeedometer } from "./FahWidgetSpeedometer";
import { FahWidgetTrendChart } from "./FahWidgetTrendChart";

const FALLBACK_RECENT_FAH_STATS: FeelingAtHomeWidgetV2Dto = {
  hasFeelAtHomeDetailsLink: false,
  status: "notStarted",
  benchmarkFeelingAtHomeScore: {
    rating: 0,
  },
  distributions: [],
};

export function FahWidget(): React.ReactNode {
  const [selectedTrendChartPeriod, setSelectedTrendChartPeriod] = useState<DateRange>("last6Months");

  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const {
    data: fahRecentStats,
    isPending: isPendingFahRecentStats,
    isError: isErrorFahRecentStats,
    error: errorFahRecentStats,
    refetch: refetchFahRecentStats,
  } = useQuery(analyticsQueries.getFah());
  const {
    data: fahHistoricStats,
    isPending: isPendingFahHistoricStats,
    isError: isErrorFahHistoricStats,
    error: fahHistoricStatsError,
    refetch: refetchFahHistoricStats,
  } = useQuery(analyticsQueries.getFahMonthlyStats(selectedTrendChartPeriod));

  const onReloadData = () => {
    void refetchFahRecentStats();
    void refetchFahHistoricStats();
  };

  const isFahNotStarted = !isPendingFahRecentStats && !isErrorFahRecentStats && fahRecentStats.status === "notStarted";

  return (
    <div className="flex w-full items-end overflow-hidden rounded-lg bg-aop-dark-blue-500 p-4 xl:p-6 xl:pr-0">
      <div className="relative flex min-w-[75%] flex-1 shrink-0 flex-col items-center overflow-hidden rounded-lg bg-white">
        {/* Title */}
        <div className="flex w-full p-4 pb-0 xl:p-6 xl:pb-0">
          <h1 className="text-headline2 xl:text-headline1">
            {`${t("component.analytics-widget.fah.title.greeting", { name: sessionUser.firstName })} 👋`}
            <span className="text-headline2-medium xl:text-headline1-medium">{` ${t("component.analytics-widget.fah.title")}`}</span>
          </h1>
        </div>
        {/* Body */}
        <div className="grid w-full grid-rows-[1fr_1fr] xl:grid-cols-2 xl:grid-rows-1 [&>:first-child]:border-b [&>:first-child]:border-aop-dark-blue-500 xl:[&>:first-child]:border-none">
          <div className="relative w-full">
            <FahWidgetSpeedometer data={fahRecentStats || FALLBACK_RECENT_FAH_STATS} />
            <AnimatePresence>
              {isPendingFahRecentStats && <AnalyticsWidgetStates.Loading />}
              {isErrorFahRecentStats && (
                <AnalyticsWidgetStates.Error onReload={onReloadData} error={errorFahRecentStats} />
              )}
            </AnimatePresence>
          </div>
          <div className="relative w-full">
            <FahWidgetTrendChart
              data={fahHistoricStats || []}
              selectedPeriod={selectedTrendChartPeriod}
              onChangePeriod={setSelectedTrendChartPeriod}
            />
            <AnimatePresence>
              {isPendingFahHistoricStats && <AnalyticsWidgetStates.Loading />}
              {isErrorFahHistoricStats && (
                <AnalyticsWidgetStates.Error onReload={onReloadData} error={fahHistoricStatsError} />
              )}
            </AnimatePresence>
          </div>
        </div>
        {/* Shared empty state */}
        <AnimatePresence>{isFahNotStarted && <EmptyState />}</AnimatePresence>
      </div>

      <div className="relative -right-8 hidden w-[350px] shrink-0 xl:block">
        <FahWidgetGraphics image={sessionUser.project.backgroundImage} />
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: "linear" }}
      className="absolute inset-0 z-20 flex flex-col items-center justify-between gap-4 bg-white/90 p-4 backdrop-blur-sm"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-center text-headline2">{t("component.analytics-widget.fah.empty.title")}</h2>
        <p className="text-center">{t("component.analytics-widget.fah.empty.description")}</p>
      </div>
      <Button type="link" href={`mailto:${SUPPORT_EMAIL}?subject=Request to start FAH survey`} isExternal>
        {t("component.analytics-widget.fah.get-help-success.btn")}
      </Button>
    </motion.div>
  );
}
